import React from 'react';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const getRollingColor = (value, day) => {
  if (day === 1) return value >= 25 ? 'success.main' : value >= 15 ? 'warning.main' : 'error.main';
  if (day === 7) return value >= 15 ? 'success.main' : value >= 8 ? 'warning.main' : 'error.main';
  if (day === 30) return value >= 5 ? 'success.main' : value >= 2 ? 'warning.main' : 'error.main';
  return 'text.primary';
};

const getOneAndDoneColor = (value) => {
  if (value >= 70) return 'error.main';
  if (value >= 50) return 'warning.main';
  return 'success.main';
};

const RateCell = ({ value, day }) => (
  <TableCell align="right" sx={{ color: getRollingColor(value, day), fontWeight: 600 }}>
    {value}%
  </TableCell>
);

const BreakdownTable = ({ title, hint, rows }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {hint}
      </Typography>
    ) : null}
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Segment</TableCell>
            <TableCell align="right">Users</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Never returned</TableCell>
            <TableCell align="right">D1+</TableCell>
            <TableCell align="right">D7+</TableCell>
            <TableCell align="right">D30+</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">
                  No cohort data yet
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.segment} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {row.segment}
                  </Typography>
                </TableCell>
                <TableCell align="right">{(row.users || 0).toLocaleString()}</TableCell>
                <TableCell align="right">{row.sharePct || 0}%</TableCell>
                <TableCell align="right" sx={{ color: getOneAndDoneColor(row.oneAndDonePct || 0), fontWeight: 600 }}>
                  {row.oneAndDonePct || 0}%
                </TableCell>
                <RateCell value={row.d1 || 0} day={1} />
                <RateCell value={row.d7 || 0} day={7} />
                <RateCell value={row.d30 || 0} day={30} />
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const CohortBreakdowns = ({ data, isLoading }) => {
  if (isLoading) {
    return <LinearProgress sx={{ mb: 3 }} />;
  }

  return (
    <Box mb={3}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
        <Typography variant="h6">
          First-session cohort breakdowns
        </Typography>
        <Chip size="small" label="D1+ / D7+ / D30+ Rolling Retention" color="info" variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Rolling retention means the player came back at any point 24 hours or more after first play —
        not that they played specifically on Day 1, 7, or 30. “Never returned” is the share whose last
        play is still on their first calendar day: the group that disappears after the first session.
        Both figures only count players who first played at least a day ago, so today’s signups never
        drag the numbers down.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <BreakdownTable
            title="By first-game result"
            hint="Win vs loss on the very first settled flip"
            rows={data?.byFirstResult}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <BreakdownTable
            title="By first stake"
            hint="Stake amount on the first bet"
            rows={data?.byFirstStake}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <BreakdownTable
            title="By radio / USSD code"
            hint="serviceCode on the first bet (web shown separately)"
            rows={data?.byServiceCode}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <BreakdownTable
            title="By acquisition source"
            hint="Player referral, affiliate/radio code, or organic signup"
            rows={data?.byAcquisition}
          />
        </Grid>
        <Grid item xs={12}>
          <BreakdownTable
            title="By Day 0 plays"
            hint="How many games they played on the calendar day they first flipped"
            rows={data?.byDay0Plays}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CohortBreakdowns;
